import * as React from 'react';

import {
  enumPaymentStatus,
  PaymentStatus,
} from '@rematter/frontend-lib-api-client';
import { MultiSelectFilter, ThemeColor } from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';

import { FilterLabel } from '../../_shared/FilterLabels';

import {
  $columnSelectedStatuses,
  $offset,
} from '~/modules/finance/_state/financePayments.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface StatusOption {
  id: string;
  label: string;
  values: PaymentStatus[];
}

const paymentStatusColorMap: Record<string, ThemeColor> = {
  [enumPaymentStatus.Issued]: 'warning',
  [enumPaymentStatus.Posted]: 'success',
  Voided: 'failure',
};

interface PaymentStatusColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ PAYMENT STATUS COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const PaymentStatusColumnFilter: React.FC<
  PaymentStatusColumnFilterProps
> = ({ onClose }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const [selectedStatusesSet, setSelectedStatusesSet] = useAtom(
    $columnSelectedStatuses,
  );
  const setOffset = useSetAtom($offset);

  // States
  const [searchQuery, setSearchQuery] = React.useState('');

  // Memoized values
  const statusOptions: StatusOption[] = React.useMemo(
    () => [
      {
        id: enumPaymentStatus.Issued,
        label: t.payments.list.status.options.issued,
        values: [enumPaymentStatus.Issued],
      },
      {
        id: enumPaymentStatus.Posted,
        label: t.payments.list.status.options.posted,
        values: [enumPaymentStatus.Posted],
      },
      {
        id: 'Voided',
        label: t.payments.list.status.options.voided,
        values: [enumPaymentStatus.Voided, enumPaymentStatus.VoidedWriteOff],
      },
    ],
    [
      t.payments.list.status.options.issued,
      t.payments.list.status.options.posted,
      t.payments.list.status.options.voided,
    ],
  );

  const selectedOptionIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const option of statusOptions) {
      if (option.values.some((v) => selectedStatusesSet.has(v))) {
        ids.add(option.id);
      }
    }
    return ids;
  }, [statusOptions, selectedStatusesSet]);

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedOptionIds),
    [selectedOptionIds],
  );

  const filteredStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [statusOptions, searchQuery],
  );

  const selectedStatusOptions = React.useMemo(
    () => statusOptions.filter((option) => selectedOptionIds.has(option.id)),
    [statusOptions, selectedOptionIds],
  );

  // Callbacks
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      const newStatuses = new Set<PaymentStatus>();
      for (const key of keys) {
        const option = statusOptions.find((o) => o.id === key);
        if (option) {
          for (const v of option.values) {
            newStatuses.add(v);
          }
        }
      }
      setSelectedStatusesSet(newStatuses);
      setOffset(0);
    },
    [setSelectedStatusesSet, setOffset, statusOptions],
  );

  const handleReset = React.useCallback(() => {
    setSelectedStatusesSet(new Set());
    setOffset(0);
  }, [setSelectedStatusesSet, setOffset]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: StatusOption, isSelected: boolean) => (
      <FilterLabel
        label={item.label}
        color={paymentStatusColorMap[item.id]}
        isSelected={isSelected}
      />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@PaymentStatusColumnFilter min-w-[250px]"
      entries={filteredStatusOptions}
      selectedEntries={selectedStatusOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(status) => status.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.payments.list.search.placeholder}
      isLoading={false}
      isFetching={false}
    />
  );
};
